import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertTriangle, LogOut } from "lucide-react";

/**
 * Silinme sürecindeki (soft-deleted) firmalar için özel ekran.
 *
 * - Sidebar / normal navigasyon YOK.
 * - Ödeme butonu YOK (deleted kullanıcı kendi kendine reaktif olamaz).
 * - Yalnızca uyarı içeriği + çıkış yap.
 * - Kalan gün ``deletion_scheduled_at`` (deleted_at + 30 gün) üzerinden
 *   dinamik hesaplanır.  Süresi geçmişse "0 gün" gösterilir; kalıcı
 *   silme tarihi de gösterilir.
 *
 * Kullanıcı Active/Passive/Archived durumdayken bu sayfaya girerse
 * uygulamaya geri yönlendirilir; deleted olmayan kimse buraya kilit
 * kalmasın.
 */
export default function AccountDeleted() {
  const { user, logout } = useAuth();

  const { daysLeft, scheduledDate } = useMemo(() => {
    const iso = user?.deletion_scheduled_at;
    if (!iso) return { daysLeft: null, scheduledDate: null };
    try {
      const target = new Date(iso);
      const now = new Date();
      const diff = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const days = Math.max(0, diff);
      const dd = String(target.getDate()).padStart(2, "0");
      const mm = String(target.getMonth() + 1).padStart(2, "0");
      const yyyy = target.getFullYear();
      return { daysLeft: days, scheduledDate: `${dd}.${mm}.${yyyy}` };
    } catch {
      return { daysLeft: null, scheduledDate: null };
    }
  }, [user?.deletion_scheduled_at]);

  // Redirect non-deleted users away so this page is not accessible by
  // mistake.  user===false → henüz auth kontrolü tamamlanmadı ya da
  // oturum yok; ProtectedRoute zaten login'e atar, burada bir şey yapma.
  if (user && user.account_status !== "deleted") {
    return <Navigate to="/" replace />;
  }
  if (!user) return null;

  const onLogout = async () => {
    try { await logout(); } catch { /* logout is best-effort */ }
    window.location.href = "/login";
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6"
      data-testid="account-deleted-page"
    >
      <div className="w-full max-w-xl rounded-2xl border border-rose-900/60 bg-slate-900/80 backdrop-blur p-8 md:p-10 shadow-2xl">
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-1 p-2.5 rounded-full bg-rose-500/15 text-rose-300">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className="text-2xl md:text-3xl font-semibold tracking-tight"
              data-testid="account-deleted-title"
            >
              Hesabınız silinme sürecinde
            </h1>
            <p
              className="mt-3 text-sm md:text-base text-slate-300 leading-relaxed"
              data-testid="account-deleted-message"
            >
              Bu hesaba ait veriler kalıcı olarak silinmeden önce{" "}
              <span className="font-semibold text-white">
                {daysLeft == null ? "yaklaşık 30" : daysLeft} gününüz
              </span>{" "}
              kaldı. Hesabınızı yeniden kullanmak istiyorsanız lütfen en kısa
              sürede Penz yetkilisiyle iletişime geçin.
            </p>

            {scheduledDate && (
              <div
                className="mt-5 text-xs uppercase tracking-[0.2em] text-slate-400"
                data-testid="account-deleted-schedule"
              >
                Kalıcı silme tarihi:{" "}
                <span className="text-slate-200 font-medium tracking-normal normal-case">
                  {scheduledDate}
                </span>
              </div>
            )}

            <div className="mt-8 space-y-3 text-xs text-slate-400 leading-relaxed border-t border-slate-800 pt-5">
              <div>
                <span className="text-slate-300 font-medium">Hesap:</span>{" "}
                {user.company_name || user.email || user.username}
              </div>
              <div>
                Hesabınızı bu ekrandan yeniden aktifleştiremezsiniz. Geri
                yükleme işlemi yalnızca Penz yönetici paneli üzerinden
                yapılabilir.
              </div>
            </div>

            <div
              className="mt-6 rounded-xl border border-slate-800 bg-slate-950/40 p-4"
              data-testid="account-deleted-contact"
            >
              <p className="text-sm text-slate-200">
                Hesabınızın silinmesini durdurmak istiyorsanız bizimle
                iletişime geçin.
              </p>
              <a
                href="mailto:support@penzapp.com"
                className="mt-2 inline-block text-sm font-medium text-rose-300 hover:text-rose-200 underline underline-offset-4 decoration-rose-500/40 hover:decoration-rose-400"
                data-testid="account-deleted-support-mailto"
              >
                support@penzapp.com
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={onLogout}
                variant="outline"
                className="rounded-lg bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
                data-testid="account-deleted-logout"
              >
                <LogOut className="w-4 h-4 mr-1.5" />
                Çıkış Yap
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
